import React, { FC, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Button, Icon } from 'src/components';
import { useAudioContext, useAudioPlayback } from 'src/context/AudioContext';
import { LearnablePronunciationData } from 'src/api/userDictionary/types';
import { useKeyboardShortcut } from 'src/hooks';
import { useIsTouchDevice } from '../hooks/useIsTouchDevice';
import { useLearnableMedia } from '../hooks/useLearnableMedia';
import { LearnableVideoButton } from './LearnableVideoButton';
import { PronunciationViewWrapper } from './PronunciationViewWrapper';
import { SpeakerCarousel, SpeakerMediaItem } from './SpeakerCarousel';
import * as SL from './layout-styles';
import * as S from './styles';

const msgs = defineMessages({
  instruction: { id: 'pronunciationBuddy_testCard_description' },
  audioAltText: { id: 'learning_session_learnables_card_audio' },
  inactiveMicrophoneTouchHint: { id: 'pronunciationBuddy_testCard_pressMicro_description' },
  activeMicrophoneTouchHint: { id: 'pronunciationBuddy_testCard_releaseMicro_description' },
  inactiveMicrophoneHint: { id: 'pronunciationBuddy_testCard_pressMicro_description_web' },
  activeMicrophoneHint: { id: 'pronunciationBuddy_testCard_releaseMicro_description_web' },
  skip: { id: 'recommended_activity_card_skip_second_CTA' },
});

export const PronunciationLearnableView: FC<{
  learnable: LearnablePronunciationData;
  progressIndicator?: React.ReactNode;
  isRecording: boolean;
  startRecording: () => void;
  stopRecording: () => void;
  onSkip?: () => void;
  /** Renders a smaller, self-contained layout without the progress indicator,
   * instruction copy or target/source text, for embedding alongside a screen
   * that already shows that information. */
  compact?: boolean;
  /** Hides the native-speaker video, for embedding alongside a screen that
   * already shows that video. */
  hideVideoButton?: boolean;
}> = ({
  learnable,
  progressIndicator,
  isRecording,
  startRecording,
  stopRecording,
  onSkip,
  compact = false,
  hideVideoButton = false,
}) => {
  const intl = useIntl();
  const [hasAudioPlayedOnce, setHasAudioPlayedOnce] = useState(false);
  const [wasPlayingAudio, setWasPlayingAudio] = useState(false);
  const isTouchDevice = useIsTouchDevice();
  const audioSrc = learnable.audios[0];
  const { play, stopAll, playing } = useAudioPlayback(audioSrc);
  const { playingAudioUrl } = useAudioContext();
  const { videoUrls, audioUrls } = useLearnableMedia(learnable.learnableIdKey, {
    enabled: !compact && !hideVideoButton,
  });

  // Outside the compact widget any speaker in the carousel can be playing, not
  // just the target audio, so the microphone waits on all of them.
  const isAudioPlaying = playing || (!compact && playingAudioUrl !== null);

  // In the standalone Pronunciation session the microphone button is always
  // full size (it's the primary action); this only controls the hint text's
  // visibility there. The embedded compact widget keeps its original
  // behaviour of growing the microphone once the (auto-played) audio has
  // finished.
  const isMicrophoneLarge = compact
    ? isRecording || (!isAudioPlaying && hasAudioPlayedOnce)
    : isRecording || !isAudioPlaying;
  const microphoneHint = intl.formatMessage(
    isTouchDevice === false
      ? isRecording
        ? msgs.activeMicrophoneHint
        : msgs.inactiveMicrophoneHint
      : isRecording
        ? msgs.activeMicrophoneTouchHint
        : msgs.inactiveMicrophoneTouchHint,
  );

  useEffect(() => {
    stopAll();
    if (!compact) {
      // Audio is on-demand only in the standalone Pronunciation session
      return;
    }
    if (audioSrc) {
      void play();
    } else {
      // This is a rare case, treat it as if the audio has already played
      setHasAudioPlayedOnce(true);
    }
  }, [compact, audioSrc, play, stopAll, setHasAudioPlayedOnce]);

  useEffect(() => {
    if (!compact) {
      return;
    }
    if (wasPlayingAudio && !playing) {
      setHasAudioPlayedOnce(true);
    }
    setWasPlayingAudio(playing);
  }, [compact, playing, wasPlayingAudio, setHasAudioPlayedOnce, setWasPlayingAudio]);

  const toggleRecording = () => {
    if (isAudioPlaying) {
      return;
    }
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      toggleRecording();
    }
  };

  useKeyboardShortcut('space', toggleRecording);

  if (compact) {
    return (
      <PronunciationViewWrapper>
        <SL.CompactLayout>
          <SL.CompactRow>
            <S.CompactAudioButton
              onClick={playing ? stopAll : play}
              aria-label={intl.formatMessage(msgs.audioAltText)}
              disabled={isRecording || !audioSrc}
            >
              <Icon name={playing ? 'wave' : 'speakerVolume3'} />
            </S.CompactAudioButton>
            <S.CompactMicrophoneButton
              $isRecording={isRecording}
              onMouseUp={isRecording ? stopRecording : startRecording}
              onKeyUp={handleKeyUp}
              onTouchStart={!isRecording ? startRecording : undefined}
              onTouchCancel={stopRecording}
              onTouchEnd={stopRecording}
              aria-label={microphoneHint}
              aria-pressed={isRecording}
              disabled={playing}
            >
              <Icon name={isRecording ? 'microphoneFilled' : 'microphoneOutline'} />
            </S.CompactMicrophoneButton>
            {!hideVideoButton && <LearnableVideoButton learnableId={learnable.learnableIdKey} />}
          </SL.CompactRow>
          {onSkip && !isRecording ? (
            <Button buttonType="secondary" onClick={onSkip}>
              {intl.formatMessage(msgs.skip)}
            </Button>
          ) : (
            <S.MicrophoneHint $isHidden={!isMicrophoneLarge}>{microphoneHint}</S.MicrophoneHint>
          )}
        </SL.CompactLayout>
      </PronunciationViewWrapper>
    );
  }

  const microphone = (
    <S.MicrophoneWrapper>
      <S.MicrophoneButton
        $isRecording={isRecording}
        onMouseUp={isRecording ? stopRecording : startRecording}
        onKeyUp={handleKeyUp}
        onTouchStart={!isRecording ? startRecording : undefined}
        onTouchCancel={stopRecording}
        onTouchEnd={stopRecording}
        aria-label={microphoneHint}
        aria-pressed={isRecording}
        disabled={isAudioPlaying}
      >
        <Icon name={isRecording ? 'microphoneFilled' : 'microphoneOutline'} />
      </S.MicrophoneButton>
      {onSkip && !isRecording ? (
        <Button buttonType="secondary" onClick={onSkip}>
          {intl.formatMessage(msgs.skip)}
        </Button>
      ) : (
        <S.MicrophoneHint $isHidden={!isMicrophoneLarge}>{microphoneHint}</S.MicrophoneHint>
      )}
    </S.MicrophoneWrapper>
  );

  // The target audio is one of the speakers, so it gets a slide of its own
  // rather than a separate control layered over the video.
  const audioClips = Array.from(new Set([...learnable.audios, ...audioUrls]));
  const speakerItems: SpeakerMediaItem[] = [
    ...videoUrls.map((url): SpeakerMediaItem => ({ type: 'video', url })),
    ...audioClips.map((url): SpeakerMediaItem => ({ type: 'audio', url })),
  ];

  if (videoUrls.length === 0) {
    return (
      <PronunciationViewWrapper>
        <SL.PronunciationLayout>
          <SL.FullRowContainer $wide>{progressIndicator}</SL.FullRowContainer>
          <SL.FullRowContainer>
            <S.InstructionText>{intl.formatMessage(msgs.instruction)}</S.InstructionText>
          </SL.FullRowContainer>
          <SL.FullRowContainer>
            <LearnableInformation learnable={learnable} />
          </SL.FullRowContainer>
          <SL.FullRowContainer>{microphone}</SL.FullRowContainer>
          <SL.FullRowContainer>
            <S.SecondaryActionsRow>
              <S.AudioButton
                onClick={playing ? stopAll : play}
                aria-label={intl.formatMessage(msgs.audioAltText)}
                disabled={isRecording || !audioSrc}
              >
                <Icon name={playing ? 'wave' : 'speakerVolume3'} />
              </S.AudioButton>
            </S.SecondaryActionsRow>
          </SL.FullRowContainer>
        </SL.PronunciationLayout>
      </PronunciationViewWrapper>
    );
  }

  return (
    <PronunciationViewWrapper>
      <SL.SessionLayout>
        <SL.FullRowContainer $wide>{progressIndicator}</SL.FullRowContainer>
        <SL.VideoColumn>
          <SpeakerCarousel key={learnable.learnableIdKey} items={speakerItems} />
        </SL.VideoColumn>
        <SL.PromptColumn>
          <S.InstructionText>{intl.formatMessage(msgs.instruction)}</S.InstructionText>
          <div>
            <LearnableInformation learnable={learnable} />
          </div>
          {microphone}
        </SL.PromptColumn>
      </SL.SessionLayout>
    </PronunciationViewWrapper>
  );
};

export const LearnableInformation: FC<{ learnable: LearnablePronunciationData }> = ({
  learnable,
}) => (
  <>
    {learnable.secondaryTargetValue && (
      <S.SecondaryTargetValue dir="auto">{learnable.secondaryTargetValue}</S.SecondaryTargetValue>
    )}
    <S.MainText dir="auto">{learnable.mainTargetValue}</S.MainText>
    <S.SourceValue dir="auto">{learnable.sourceDefinitionValue}</S.SourceValue>
  </>
);
